import { useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "urql"

import "../graphql/customerSearch"
import { SALES_SEARCH_LIMIT } from "../constants"
import type { SalesCustomer } from "../types"

import { GetSalesCustomerListDocument } from "@/graphql/codegen/graphql"
import { COMPANY_TYPE } from "@/screens/Companies/constants"

const SALES_CUSTOMER_COMPANY_TYPES = [COMPANY_TYPE.CUSTOMER]

interface UseGetSalesCustomersReturn {
  customers: SalesCustomer[]
  isFetching: boolean
  loadMore: () => void
}

export const useGetSalesCustomers = (searchQuery: string): UseGetSalesCustomersReturn => {
  const [offset, setOffset] = useState(0)
  const [allCustomers, setAllCustomers] = useState<SalesCustomer[]>([])

  useEffect(() => {
    setOffset(0)
    setAllCustomers([])
  }, [searchQuery])

  const [{ data, fetching }] = useQuery({
    query: GetSalesCustomerListDocument,
    variables: {
      input: {
        searchTerm: searchQuery || undefined,
        companyTypes: SALES_CUSTOMER_COMPANY_TYPES,
        limit: SALES_SEARCH_LIMIT,
        offset,
      },
    },
    pause: !searchQuery,
  })

  const currentPage = useMemo(() => {
    if (data?.location?.listBillingLocations?.__typename === "BillingLocationListSuccess") {
      return data.location.listBillingLocations.billingLocations
    }
    return null
  }, [data])

  useEffect(() => {
    if (!currentPage) return
    setAllCustomers((prev) => (offset === 0 ? currentPage : [...prev, ...currentPage]))
  }, [currentPage, offset])

  const hasMore = currentPage !== null && currentPage.length === SALES_SEARCH_LIMIT

  const loadMore = useCallback(() => {
    if (!fetching && hasMore) {
      setOffset((prev) => prev + SALES_SEARCH_LIMIT)
    }
  }, [fetching, hasMore])

  return { customers: allCustomers, isFetching: fetching, loadMore }
}
